import { BarChart3 } from "lucide-react";
import { cn } from "@/lib/utils";
import type { ChatContextUsage } from "@/types/chat.type";
import { computeMessageCostUsd } from "./messageCost";

/** Combined input+output cost (USD) at or above which the badge alerts in red. */
const COST_ALERT_THRESHOLD_USD = 2;

function formatTokenCount(n: number): string {
  return n.toLocaleString("en-US");
}

/** USD with enough precision for sub-cent per-message costs. */
function formatUsd(n: number | undefined): string {
  if (typeof n !== "number" || !Number.isFinite(n) || n <= 0) return "$0.00";
  if (n < 0.0001) return "<$0.0001";
  if (n < 0.01) return `$${n.toFixed(4)}`;
  return `$${n.toFixed(2)}`;
}

export function getMessageUsage(
  metadata: Record<string, unknown> | null,
): ChatContextUsage | null {
  const raw = metadata?.usage;
  if (!raw || typeof raw !== "object") return null;
  const { inputTokens, outputTokens, contextTokens, model, inputCostUsd, outputCostUsd } =
    raw as ChatContextUsage;
  if (
    typeof inputTokens !== "number" ||
    typeof outputTokens !== "number" ||
    typeof contextTokens !== "number"
  ) {
    return null;
  }
  return {
    inputTokens,
    outputTokens,
    contextTokens,
    ...(typeof model === "string" ? { model } : {}),
    ...(typeof inputCostUsd === "number" ? { inputCostUsd } : {}),
    ...(typeof outputCostUsd === "number" ? { outputCostUsd } : {}),
  };
}

type MessageUsageBadgeProps = {
  metadata: Record<string, unknown> | null;
  className?: string;
};

/** Per-message token usage indicator shown after an assistant reply completes. */
export default function MessageUsageBadge({ metadata, className }: MessageUsageBadgeProps) {
  const usage = getMessageUsage(metadata);
  if (!usage) return null;

  // Prefer the backend-stamped cost; fall back to a client-side estimate from the
  // model + token counts so the price shows even on messages persisted before the
  // backend captured costs. Absent only when the model has no known pricing.
  const estimated = computeMessageCostUsd(usage.model, usage.inputTokens, usage.outputTokens);
  const inputCostUsd =
    typeof usage.inputCostUsd === "number" ? usage.inputCostUsd : estimated?.inputCostUsd;
  const outputCostUsd =
    typeof usage.outputCostUsd === "number" ? usage.outputCostUsd : estimated?.outputCostUsd;
  const hasCost = typeof inputCostUsd === "number" && typeof outputCostUsd === "number";
  const totalCostUsd = (inputCostUsd ?? 0) + (outputCostUsd ?? 0);
  const isExpensive = hasCost && totalCostUsd >= COST_ALERT_THRESHOLD_USD;

  const inputLabel = `Input ${formatTokenCount(usage.inputTokens)} tokens${
    hasCost ? ` (${formatUsd(inputCostUsd)})` : ""
  }`;
  const outputLabel = `output ${formatTokenCount(usage.outputTokens)} tokens${
    hasCost ? ` (${formatUsd(outputCostUsd)})` : ""
  }`;
  const ariaLabel = `${inputLabel}, ${outputLabel}${
    isExpensive ? ` — high cost, total ${formatUsd(totalCostUsd)}` : ""
  }`;

  return (
    <div className={cn("mt-1.5 flex justify-end", className)}>
      <span
        className="group relative inline-flex cursor-default items-center outline-none"
        role="img"
        aria-label={ariaLabel}
        tabIndex={0}
      >
        <BarChart3
          className={cn(
            "size-3.5 transition-colors",
            isExpensive
              ? "text-red-500 group-hover:text-red-400 group-focus:text-red-400"
              : "text-co-faint group-hover:text-co-muted group-focus:text-co-muted",
          )}
        />

        <span
          role="tooltip"
          className={cn(
            "pointer-events-none absolute bottom-full right-0 z-50 mb-1.5 w-max max-w-[240px]",
            "rounded-none border border-co-line2 bg-[var(--co-dropdown-panel-bg)] px-2.5 py-2 shadow-[var(--shell-shadow)]",
            "opacity-0 transition-opacity duration-100",
            "group-hover:opacity-100 group-focus:opacity-100",
          )}
        >
          <span className="block text-[11px] font-medium text-co-text2">Token usage</span>
          {usage.model ? (
            <span className="mt-0.5 block truncate text-[10px] leading-snug text-co-faint">
              {usage.model}
            </span>
          ) : null}
          <span className="mt-1 block text-[10px] leading-snug text-co-muted">
            Input: {formatTokenCount(usage.inputTokens)}
            {hasCost ? (
              <span className="text-co-faint"> · {formatUsd(inputCostUsd)}</span>
            ) : null}
          </span>
          <span className="block text-[10px] leading-snug text-co-muted">
            Output: {formatTokenCount(usage.outputTokens)}
            {hasCost ? (
              <span className="text-co-faint"> · {formatUsd(outputCostUsd)}</span>
            ) : null}
          </span>
          {hasCost ? (
            <span
              className={cn(
                "mt-1 block border-t border-co-line pt-1 text-[10px] leading-snug",
                isExpensive ? "font-medium text-red-400" : "text-co-muted",
              )}
            >
              Total: {formatUsd(totalCostUsd)}
            </span>
          ) : null}
        </span>
      </span>
    </div>
  );
}
